import { Progress } from '../components/ui/progress';
import { ColumnDef } from '@tanstack/react-table';
import { AlertCircle, AlertTriangle, CheckCircle, Clock, Pause, Play, RefreshCw, Trash2, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Header from '../components/Header';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { DataTable } from '../components/ui/data-table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  roles: Array<{ key: string; name: string; description: string }>;
}

interface SyncRecord {
  id: string;
  status: 'pending' | 'in_progress' | 'paused' | 'completed' | 'failed';
  sync_type: 'manual' | 'webhook' | 'scheduled';
  started_at: string | null;
  completed_at: string | null;
  items_synced: number;
  items_failed: number;
  categories_synced: number;
  images_synced: number;
  total_products?: number | null;
  products_synced?: number | null;
  error_log: Array<{ item: string; error: string; timestamp: string }> | null;
  created_at: string;
}

interface CurrentStatus {
  syncId: string;
  status: 'pending' | 'in_progress' | 'paused' | 'completed' | 'failed';
  itemsSynced: number;
  itemsFailed: number;
  categoriesSynced: number;
  imagesSynced: number;
  totalProducts?: number;
  /** Shopify products finished; itemsSynced is templates (one per variant) */
  productsSynced?: number;
}

interface CleanupPreview {
  tables: Array<{ table: string; count: number }>;
  qrCodesToUnlink: number;
  totalRecords: number;
  isClean: boolean;
}

const statusConfig = {
  pending: { icon: Clock, label: 'Pending', variant: 'secondary' as const, color: 'text-muted-foreground' },
  in_progress: { icon: RefreshCw, label: 'In Progress', variant: 'default' as const, color: 'text-blue-600' },
  paused: { icon: Pause, label: 'Paused', variant: 'secondary' as const, color: 'text-yellow-600' },
  completed: { icon: CheckCircle, label: 'Completed', variant: 'default' as const, color: 'text-green-600' },
  failed: { icon: XCircle, label: 'Failed', variant: 'destructive' as const, color: 'text-red-600' },
};

export default function ShopifySync() {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [syncHistory, setSyncHistory] = useState<SyncRecord[]>([]);
  const [currentStatus, setCurrentStatus] = useState<CurrentStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [pausing, setPausing] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [tagsFilter, setTagsFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'active' | 'draft' | 'all'>('active');
  const [skipWithoutImages, setSkipWithoutImages] = useState(false);

  // Cleanup state
  const [cleanupDialogOpen, setCleanupDialogOpen] = useState(false);
  const [cleanupPreview, setCleanupPreview] = useState<CleanupPreview | null>(null);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupExecuting, setCleanupExecuting] = useState(false);
  const [confirmationPhrase, setConfirmationPhrase] = useState('');
  const [triggerSyncAfterCleanup, setTriggerSyncAfterCleanup] = useState(false);
  const [historyLogRecord, setHistoryLogRecord] = useState<SyncRecord | null>(null);
  const [cleanupAcknowledgements, setCleanupAcknowledgements] = useState({
    understand: false,
    irreversible: false,
    affectsAll: false,
  });

  useEffect(() => {
    checkAuth();
    loadSyncHistory();
    loadCurrentStatus();
  }, []);

  useEffect(() => {
    const active = currentStatus?.status === 'in_progress' || currentStatus?.status === 'pending';
    if (!active) return;

    loadCurrentStatus();
    const interval = setInterval(() => {
      loadCurrentStatus();
      loadSyncHistory();
    }, 2000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStatus?.status, currentStatus?.syncId]);

  const checkAuth = () => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    if (!token || !userData) {
      navigate('/login');
      return;
    }

    const parsedUser = JSON.parse(userData);
    setUser(parsedUser);

    const isAdmin = parsedUser.roles?.some((r: any) => r.key === 'admin');
    if (!isAdmin) {
      navigate('/dashboard');
    }
  };

  const loadSyncHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/shopify/sync-history?limit=10', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const result = await response.json();
        setSyncHistory(result.data || []);
      }
    } catch (error) {
      console.error('Failed to load sync history:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCurrentStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/shopify/sync-status', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const result = await response.json();
        setCurrentStatus(result.data);
      }
    } catch (error) {
      console.error('Failed to load sync status:', error);
    }
  };

  const handleStartSync = async () => {
    setConfirmDialogOpen(false);
    setSyncing(true);

    try {
      const token = localStorage.getItem('token');
      const tags = tagsFilter.trim() ? tagsFilter.split(',').map(t => t.trim()).filter(Boolean) : undefined;
      
      const response = await fetch('/api/shopify/sync', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ fullSync: true, tags, status: statusFilter, skipWithoutImages }),
      });

      if (response.ok) {
        const result = await response.json();
        setCurrentStatus(result.data);

        // Reload history after a short delay
        setTimeout(() => {
          loadSyncHistory();
        }, 1000);
      } else {
        const error = await response.json();
        alert(`Failed to start sync: ${error.message}`);
      }
    } catch (error) {
      console.error('Failed to start sync:', error);
      alert('Failed to start sync. Please try again.');
    } finally {
      setSyncing(false);
    }
  };

  const handlePauseSync = async () => {
    setPausing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/shopify/sync/pause', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        // Poll for updated status
        setTimeout(() => {
          loadCurrentStatus();
          loadSyncHistory();
        }, 1000);
      } else {
        const error = await response.json();
        alert(`Failed to pause sync: ${error.message}`);
      }
    } catch (error) {
      console.error('Failed to pause sync:', error);
      alert('Failed to pause sync. Please try again.');
    } finally {
      setPausing(false);
    }
  };

  const handleResumeSync = async () => {
    if (!currentStatus?.syncId) return;

    setResuming(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/shopify/sync/${currentStatus.syncId}/resume`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setCurrentStatus(result.data);
        setTimeout(() => loadSyncHistory(), 1000);
      } else {
        const error = await response.json();
        alert(`Failed to resume sync: ${error.message}`);
      }
    } catch (error) {
      console.error('Failed to resume sync:', error);
      alert('Failed to resume sync. Please try again.');
    } finally {
      setResuming(false);
    }
  };

  const openCleanupDialog = async () => {
    setCleanupDialogOpen(true);
    setCleanupLoading(true);
    setConfirmationPhrase('');
    setCleanupAcknowledgements({ understand: false, irreversible: false, affectsAll: false });
    setTriggerSyncAfterCleanup(false);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/shopify/cleanup/preview', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const result = await response.json();
        setCleanupPreview(result.data);
      } else {
        const error = await response.json();
        alert(`Failed to load cleanup preview: ${error.message}`);
        setCleanupDialogOpen(false);
      }
    } catch (error) {
      console.error('Failed to load cleanup preview:', error);
      alert('Failed to load cleanup preview.');
      setCleanupDialogOpen(false);
    } finally {
      setCleanupLoading(false);
    }
  };

  const handleExecuteCleanup = async () => {
    if (confirmationPhrase !== 'DELETE ALL DATA') {
      alert('Please type the exact confirmation phrase: DELETE ALL DATA');
      return;
    }

    if (!cleanupAcknowledgements.understand || !cleanupAcknowledgements.irreversible || !cleanupAcknowledgements.affectsAll) {
      alert('Please acknowledge all warnings before proceeding.');
      return;
    }

    setCleanupExecuting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/shopify/cleanup/execute', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          confirmationPhrase,
          triggerSync: triggerSyncAfterCleanup,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        alert(`Cleanup complete!\n\nDeleted ${result.data.totalDeleted} records.\nUnlinked ${result.data.qrCodesUnlinked} QR codes.${result.data.syncTriggered ? '\n\nA fresh Shopify sync has been started.' : ''}`);
        setCleanupDialogOpen(false);
        loadSyncHistory();
        loadCurrentStatus();
      } else {
        const error = await response.json();
        alert(`Cleanup failed: ${error.message}`);
      }
    } catch (error) {
      console.error('Failed to execute cleanup:', error);
      alert('Cleanup failed. Please try again.');
    } finally {
      setCleanupExecuting(false);
    }
  };

  const allAcknowledged = cleanupAcknowledgements.understand && cleanupAcknowledgements.irreversible && cleanupAcknowledgements.affectsAll;
  const canExecuteCleanup = allAcknowledged && confirmationPhrase === 'DELETE ALL DATA' && !cleanupExecuting;

  const syncActive = currentStatus?.status === 'in_progress' || currentStatus?.status === 'pending';
  const totalProducts = currentStatus?.totalProducts ?? 0;
  const productsSynced = currentStatus?.productsSynced ?? 0;
  const productProgressPct =
    totalProducts > 0 ? Math.min(100, Math.round((productsSynced / totalProducts) * 100)) : undefined;

  const columns: ColumnDef<SyncRecord>[] = [
    {
      accessorKey: 'created_at',
      header: 'Started',
      cell: ({ row }) => {
        const date = new Date(row.original.created_at);
        return <span>{date.toLocaleString()}</span>;
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const status = row.original.status;
        const config = statusConfig[status];
        const Icon = config.icon;

        return (
          <Badge variant={config.variant} className="flex items-center gap-1 w-fit">
            <Icon className={`h-3 w-3 ${config.color}`} />
            {config.label}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'sync_type',
      header: 'Type',
      cell: ({ row }) => (
        <span className="capitalize">{row.original.sync_type}</span>
      ),
    },
    {
      accessorKey: 'categories_synced',
      header: 'Categories',
      cell: ({ row }) => (
        <span className="font-mono">{row.original.categories_synced}</span>
      ),
    },
    {
      id: 'products',
      header: 'Products',
      cell: ({ row }) => {
        const total = row.original.total_products;
        const done = row.original.products_synced;
        if (total == null || total === 0) return <span className="text-muted-foreground">—</span>;
        return <span className="font-mono">{done ?? 0} / {total}</span>;
      },
    },
    {
      accessorKey: 'items_synced',
      header: 'Templates',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <span className="font-mono text-green-600">{row.original.items_synced}</span>
          {row.original.items_failed > 0 && (
            <span className="font-mono text-red-600">({row.original.items_failed} failed)</span>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'images_synced',
      header: 'Images',
      cell: ({ row }) => (
        <span className="font-mono">{row.original.images_synced}</span>
      ),
    },
    {
      accessorKey: 'completed_at',
      header: 'Completed',
      cell: ({ row }) => {
        if (!row.original.completed_at) return <span className="text-muted-foreground">-</span>;
        const date = new Date(row.original.completed_at);
        return <span>{date.toLocaleString()}</span>;
      },
    },
    {
      id: 'failure_message',
      header: 'Error',
      cell: ({ row }) => {
        const r = row.original;
        if (r.status !== 'failed') return <span className="text-muted-foreground">—</span>;
        const log = r.error_log ?? [];
        if (log.length === 0) return <span className="text-muted-foreground">—</span>;
        const last = log[log.length - 1];
        const text = last?.error ?? '—';
        return (
          <div className="flex flex-col gap-1 max-w-md">
            <span className="text-sm text-destructive line-clamp-2" title={text}>
              {text}
            </span>
            {log.length > 1 && (
              <Button
                variant="link"
                className="h-auto p-0 text-xs justify-start"
                onClick={() => setHistoryLogRecord(r)}
              >
                View all ({log.length} entries)
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} />

      <main className="container mx-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-heading font-bold text-primary mb-2">Shopify Sync</h1>
            <p className="text-muted-foreground">
              Sync products, variants, categories, and images from Shopify
            </p>
          </div>

          <div className="flex items-center gap-2">
            {currentStatus?.status === 'in_progress' && (
              <Button
                variant="secondary"
                onClick={handlePauseSync}
                disabled={pausing}
                className="flex items-center gap-2"
              >
                <Pause className="h-4 w-4" />
                {pausing ? 'Pausing...' : 'Pause'}
              </Button>
            )}

            {currentStatus?.status === 'paused' && (
              <Button
                onClick={handleResumeSync}
                disabled={resuming}
                className="flex items-center gap-2"
              >
                <Play className="h-4 w-4" />
                {resuming ? 'Resuming...' : 'Resume'}
              </Button>
            )}

            <Button
              onClick={() => setConfirmDialogOpen(true)}
              disabled={syncing || currentStatus?.status === 'in_progress' || currentStatus?.status === 'paused'}
              className="flex items-center gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${currentStatus?.status === 'in_progress' ? 'animate-spin' : ''}`} />
              {currentStatus?.status === 'in_progress' ? 'Syncing...' : 'Start Sync'}
            </Button>
          </div>
        </div>

        {currentStatus && (
          <Card className="bg-card p-6 mb-6">
            <h2 className="text-xl font-heading font-semibold mb-4">Current Sync Status</h2>

            {syncActive && (
              <div className="mb-6 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="font-medium">
                    {totalProducts > 0 ? 'Product sync progress' : 'Preparing sync…'}
                  </span>
                  <span className="font-mono text-muted-foreground">
                    {totalProducts > 0
                      ? `${productsSynced} / ${totalProducts} products (${productProgressPct}%)`
                      : 'Counting products in Shopify…'}
                  </span>
                </div>
                <Progress value={productProgressPct ?? (syncActive ? 5 : 0)} className="h-3" />
                <p className="text-xs text-muted-foreground">
                  {currentStatus.itemsSynced} templates (variants) synced
                  {' · '}{currentStatus.imagesSynced} images
                  {currentStatus.itemsFailed > 0 && (
                    <span className="text-destructive"> · {currentStatus.itemsFailed} failed</span>
                  )}
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Status</p>
                <div className="flex items-center gap-2">
                  {(() => {
                    const config = statusConfig[currentStatus.status];
                    const Icon = config.icon;
                    return (
                      <>
                        <Icon className={`h-4 w-4 ${config.color} ${currentStatus.status === 'in_progress' ? 'animate-spin' : ''}`} />
                        <span className="font-medium">{config.label}</span>
                      </>
                    );
                  })()}
                </div>
              </div>

              <div>
                <p className="text-sm text-muted-foreground mb-1">Products</p>
                <p className="text-2xl font-bold">
                  {totalProducts > 0
                    ? `${productsSynced} / ${totalProducts}`
                    : syncActive
                      ? '…'
                      : '—'}
                </p>
              </div>

              <div>
                <p className="text-sm text-muted-foreground mb-1">Categories</p>
                <p className="text-2xl font-bold">{currentStatus.categoriesSynced}</p>
              </div>

              <div>
                <p className="text-sm text-muted-foreground mb-1">Templates (per variant)</p>
                <p className="text-2xl font-bold text-green-600">{currentStatus.itemsSynced}</p>
              </div>

              <div>
                <p className="text-sm text-muted-foreground mb-1">Images</p>
                <p className="text-2xl font-bold">{currentStatus.imagesSynced}</p>
              </div>

              <div>
                <p className="text-sm text-muted-foreground mb-1">Failed</p>
                <p className={`text-2xl font-bold ${currentStatus.itemsFailed > 0 ? 'text-red-600' : 'text-muted-foreground'}`}>
                  {currentStatus.itemsFailed}
                </p>
              </div>
            </div>
          </Card>
        )}

        <Card className="bg-card p-6">
          <h2 className="text-xl font-heading font-semibold mb-4">Sync History</h2>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <DataTable columns={columns} data={syncHistory} />
          )}
        </Card>

        {/* Danger Zone */}
        <Card className="border-destructive bg-destructive/5 p-6 mt-6">
          <div className="flex items-start gap-4">
            <div className="rounded-full bg-destructive/10 p-3">
              <AlertTriangle className="h-6 w-6 text-destructive" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-heading font-semibold text-destructive mb-2">Danger Zone</h2>
              <p className="text-muted-foreground mb-4">
                These operations are destructive and cannot be undone. Use with extreme caution.
              </p>

              <div className="border border-destructive/30 rounded-lg p-4 bg-background">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-foreground">Clean All Shopify Data</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Delete all synced products, categories, orders, quotes, jobs, and related data.
                      QR codes will be unlinked but preserved.
                    </p>
                  </div>
                  <Button
                    variant="destructive"
                    onClick={openCleanupDialog}
                    disabled={currentStatus?.status === 'in_progress'}
                    className="flex items-center gap-2 ml-4"
                  >
                    <Trash2 className="h-4 w-4" />
                    Clean Data
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </main>

      {/* Cleanup Confirmation Dialog */}
      <Dialog open={cleanupDialogOpen} onOpenChange={setCleanupDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Dangerous Operation: Delete All Data
            </DialogTitle>
            <DialogDescription>
              This will permanently delete all Shopify-synced data from the database.
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          {cleanupLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : cleanupPreview?.isClean ? (
            <div className="py-8 text-center">
              <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">Database is already clean</h3>
              <p className="text-muted-foreground">There is no data to delete.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Data to be deleted */}
              <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4">
                <h4 className="font-medium text-destructive mb-2">Data that will be PERMANENTLY DELETED:</h4>
                <div className="max-h-40 overflow-y-auto space-y-1 text-sm">
                  {cleanupPreview?.tables.map(({ table, count }) => (
                    <div key={table} className="flex justify-between">
                      <span className="font-mono text-muted-foreground">{table}</span>
                      <span className="font-mono text-destructive">{count.toLocaleString()} records</span>
                    </div>
                  ))}
                  {cleanupPreview?.qrCodesToUnlink ? (
                    <div className="flex justify-between pt-2 border-t mt-2">
                      <span className="font-mono text-muted-foreground">qr_codes (unlink only)</span>
                      <span className="font-mono text-yellow-600">{cleanupPreview.qrCodesToUnlink.toLocaleString()} to unlink</span>
                    </div>
                  ) : null}
                </div>
                <div className="flex justify-between pt-3 border-t mt-3 font-semibold">
                  <span>Total records to delete:</span>
                  <span className="text-destructive">{cleanupPreview?.totalRecords.toLocaleString()}</span>
                </div>
              </div>

              {/* Acknowledgements */}
              <div className="space-y-3">
                <h4 className="font-medium">You must acknowledge the following:</h4>
                
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="ack-understand"
                    checked={cleanupAcknowledgements.understand}
                    onCheckedChange={(checked) => setCleanupAcknowledgements(prev => ({ ...prev, understand: checked === true }))}
                  />
                  <Label htmlFor="ack-understand" className="text-sm cursor-pointer">
                    I understand this will delete <strong>all products, categories, orders, quotes, jobs, shipments, tasks</strong>, and related data.
                  </Label>
                </div>

                <div className="flex items-start gap-3">
                  <Checkbox
                    id="ack-irreversible"
                    checked={cleanupAcknowledgements.irreversible}
                    onCheckedChange={(checked) => setCleanupAcknowledgements(prev => ({ ...prev, irreversible: checked === true }))}
                  />
                  <Label htmlFor="ack-irreversible" className="text-sm cursor-pointer">
                    I understand this action is <strong>irreversible</strong> and there is no way to recover deleted data.
                  </Label>
                </div>

                <div className="flex items-start gap-3">
                  <Checkbox
                    id="ack-affects-all"
                    checked={cleanupAcknowledgements.affectsAll}
                    onCheckedChange={(checked) => setCleanupAcknowledgements(prev => ({ ...prev, affectsAll: checked === true }))}
                  />
                  <Label htmlFor="ack-affects-all" className="text-sm cursor-pointer">
                    I understand this affects <strong>all users</strong> of this system, not just me.
                  </Label>
                </div>
              </div>

              {/* Confirmation phrase */}
              <div>
                <Label htmlFor="confirmation-phrase" className="font-medium">
                  Type <code className="bg-muted px-2 py-1 rounded text-destructive font-bold">DELETE ALL DATA</code> to confirm:
                </Label>
                <Input
                  id="confirmation-phrase"
                  value={confirmationPhrase}
                  onChange={(e) => setConfirmationPhrase(e.target.value)}
                  placeholder="Type the phrase exactly..."
                  className="mt-2 font-mono"
                  autoComplete="off"
                />
              </div>

              {/* Optional: trigger sync after */}
              <div className="flex items-center gap-3 pt-2 border-t">
                <Checkbox
                  id="trigger-sync"
                  checked={triggerSyncAfterCleanup}
                  onCheckedChange={(checked) => setTriggerSyncAfterCleanup(checked === true)}
                />
                <Label htmlFor="trigger-sync" className="text-sm cursor-pointer">
                  Start a fresh Shopify sync after cleanup completes
                </Label>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="secondary" onClick={() => setCleanupDialogOpen(false)} disabled={cleanupExecuting}>
              Cancel
            </Button>
            {!cleanupPreview?.isClean && (
              <Button
                variant="destructive"
                onClick={handleExecuteCleanup}
                disabled={!canExecuteCleanup}
                className="gap-2"
              >
                {cleanupExecuting ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4" />
                    Delete All Data
                  </>
                )}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!historyLogRecord} onOpenChange={(open) => !open && setHistoryLogRecord(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Sync log</DialogTitle>
            <DialogDescription>
              {historyLogRecord
                ? `Started ${new Date(historyLogRecord.created_at).toLocaleString()} · ${historyLogRecord.status}`
                : ''}
            </DialogDescription>
          </DialogHeader>
          <ul className="text-sm space-y-3 overflow-y-auto flex-1 min-h-0 pr-1">
            {historyLogRecord?.error_log?.map((e, i) => (
              <li key={i} className="border-b pb-3 last:border-0">
                <div className="text-muted-foreground text-xs">
                  {e.timestamp ? new Date(e.timestamp).toLocaleString() : '—'}
                </div>
                <div className="font-medium mt-1">{e.item}</div>
                <div className="text-destructive mt-1 whitespace-pre-wrap break-words">{e.error}</div>
              </li>
            ))}
          </ul>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Start Shopify Sync?</DialogTitle>
            <DialogDescription>
              This will sync all products, variants, categories, and images from Shopify.
              The process may take several minutes depending on the size of your catalog.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mb-4">
            <div>
              <Label htmlFor="status-filter">Product Status</Label>
              <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as 'active' | 'draft' | 'all')}>
                <SelectTrigger id="status-filter" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active only</SelectItem>
                  <SelectItem value="draft">Draft only</SelectItem>
                  <SelectItem value="all">Active + Draft</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Choose which product status to pull from Shopify.
              </p>
            </div>

            <div>
              <Label htmlFor="tags-filter">Filter by Tags (optional)</Label>
              <Input
                id="tags-filter"
                placeholder="e.g., furniture, staging, rental"
                value={tagsFilter}
                onChange={(e) => setTagsFilter(e.target.value)}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Comma-separated list of Shopify product tags. Leave empty to sync all products.
              </p>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="skip-no-images"
                checked={skipWithoutImages}
                onCheckedChange={(checked) => setSkipWithoutImages(checked === true)}
              />
              <Label htmlFor="skip-no-images" className="cursor-pointer">
                Skip products without images
              </Label>
            </div>
          </div>

          <div className="bg-muted p-4 rounded-md mb-4">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium mb-1">What will be synced:</p>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  <li>
                    Status: <strong>{statusFilter === 'all' ? 'Active + Draft' : statusFilter}</strong>
                    {tagsFilter.trim() && <> with tags: <strong>{tagsFilter}</strong></>}
                  </li>
                  {skipWithoutImages && <li>Products without images will be <strong>skipped</strong></li>}
                  <li>Collections will be created/updated as categories</li>
                  <li>Each product variant will create an Asset Template</li>
                  <li>Product images will be downloaded and uploaded to R2</li>
                  <li>Pricing and specifications will be updated</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="secondary" onClick={() => setConfirmDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleStartSync} disabled={syncing}>
              {syncing ? 'Starting...' : 'Start Sync'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

